// src/pages/AdminGasPropanePage.js
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert, Badge, Button, Form, Modal, Spinner, Table,
} from "react-bootstrap";
import api from "../api";

const EMPTY_FORM = { name: "", address: "", city: "", is_gas: false, is_propane: false };

export default function AdminGasPropanePage() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [actionError, setActionError] = useState("");

  const [modal, setModal]   = useState(null); // "add" | "edit" | "delete"
  const [target, setTarget] = useState(null);
  const [form, setForm]     = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await api.get("/admin/gas-propane");
      setLocations(res.data.items);
    } catch {
      setError("Failed to load locations.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function field(key) {
    return (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  }

  function toggle(key) {
    return (e) => setForm((p) => ({ ...p, [key]: e.target.checked }));
  }

  function openAdd() {
    setForm(EMPTY_FORM);
    setActionError("");
    setTarget(null);
    setModal("add");
  }

  function openEdit(loc) {
    setForm({
      name:       loc.name,
      address:    loc.address || "",
      city:       loc.city || "",
      is_gas:     loc.is_gas,
      is_propane: loc.is_propane,
    });
    setActionError("");
    setTarget(loc);
    setModal("edit");
  }

  function openDelete(loc) {
    setTarget(loc);
    setActionError("");
    setModal("delete");
  }

  function closeModal() {
    setModal(null);
    setActionError("");
  }

  async function handleAdd(e) {
    e.preventDefault();
    if (!form.name.trim()) { setActionError("Name is required."); return; }
    if (!form.is_gas && !form.is_propane) { setActionError("Select at least Gas or Propane."); return; }
    setSaving(true);
    setActionError("");
    try {
      await api.post("/admin/gas-propane", {
        name:       form.name.trim(),
        address:    form.address.trim(),
        city:       form.city.trim(),
        is_gas:     form.is_gas,
        is_propane: form.is_propane,
      });
      closeModal();
      await load();
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to create location.");
    } finally {
      setSaving(false);
    }
  }

  async function handleEdit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setActionError("Name is required."); return; }
    if (!form.is_gas && !form.is_propane) { setActionError("Select at least Gas or Propane."); return; }
    setSaving(true);
    setActionError("");
    try {
      await api.put("/admin/gas-propane", {
        gas_id:     target.gas_id,
        propane_id: target.propane_id,
        name:       form.name.trim(),
        address:    form.address.trim(),
        city:       form.city.trim(),
        is_gas:     form.is_gas,
        is_propane: form.is_propane,
      });
      closeModal();
      await load();
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to update location.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    setActionError("");
    try {
      await api.delete("/admin/gas-propane", {
        data: { gas_id: target.gas_id, propane_id: target.propane_id },
      });
      closeModal();
      await load();
    } catch {
      setActionError("Failed to delete location.");
    } finally {
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <div className="p-3">
        <Spinner animation="border" size="sm" className="me-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="d-flex align-items-center mb-4 gap-3">
        <Link to="/admin" className="btn btn-outline-secondary btn-sm">
          <i className="bi bi-arrow-left me-1" />Back
        </Link>
        <h3 className="mb-0">
          <i className="bi bi-fire me-2" />
          Gas &amp; Propane Locations
        </h3>
        <Button size="sm" className="ms-auto" onClick={openAdd}>
          <i className="bi bi-plus-lg me-1" />Add Location
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {locations.length === 0 && !error ? (
        <p className="text-muted">No locations have been added yet.</p>
      ) : (
        <Table hover responsive size="sm">
          <thead>
            <tr>
              <th>Name</th>
              <th>Address</th>
              <th>City</th>
              <th>Type</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {locations.map((loc) => {
              const key = `${loc.gas_id ?? "g0"}-${loc.propane_id ?? "p0"}`;
              return (
                <tr key={key}>
                  <td className="align-middle fw-semibold">{loc.name}</td>
                  <td className="align-middle text-muted">{loc.address || <span className="fst-italic">—</span>}</td>
                  <td className="align-middle text-muted">{loc.city || <span className="fst-italic">—</span>}</td>
                  <td className="align-middle">
                    <div className="d-flex gap-1 flex-wrap">
                      {loc.is_gas     && <Badge bg="warning" text="dark">Gas</Badge>}
                      {loc.is_propane && <Badge bg="info"    text="dark">Propane</Badge>}
                    </div>
                  </td>
                  <td className="align-middle">
                    <div className="d-flex gap-2">
                      <Button size="sm" variant="outline-primary" onClick={() => openEdit(loc)}>Edit</Button>
                      <Button size="sm" variant="outline-danger"  onClick={() => openDelete(loc)}>Delete</Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {/* Add Modal */}
      <Modal show={modal === "add"} onHide={closeModal} centered>
        <Form onSubmit={handleAdd}>
          <Modal.Header closeButton>
            <Modal.Title>Add Location</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {actionError && <Alert variant="danger" className="py-2">{actionError}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Name <span className="text-danger">*</span></Form.Label>
              <Form.Control value={form.name} onChange={field("name")} placeholder="e.g. Kwik Fill" autoFocus />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control value={form.address} onChange={field("address")} placeholder="123 Main St" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>City</Form.Label>
              <Form.Control value={form.city} onChange={field("city")} placeholder="Clare" />
            </Form.Group>
            <Form.Group>
              <Form.Label>Type <span className="text-danger">*</span></Form.Label>
              <div className="d-flex gap-4 mt-1">
                <Form.Check type="checkbox" id="add-is-gas"     label="Gas"     checked={form.is_gas}     onChange={toggle("is_gas")} />
                <Form.Check type="checkbox" id="add-is-propane" label="Propane" checked={form.is_propane} onChange={toggle("is_propane")} />
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Add Location"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal show={modal === "edit"} onHide={closeModal} centered>
        <Form onSubmit={handleEdit}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Location</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {actionError && <Alert variant="danger" className="py-2">{actionError}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Name <span className="text-danger">*</span></Form.Label>
              <Form.Control value={form.name} onChange={field("name")} autoFocus />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control value={form.address} onChange={field("address")} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>City</Form.Label>
              <Form.Control value={form.city} onChange={field("city")} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Type <span className="text-danger">*</span></Form.Label>
              <div className="d-flex gap-4 mt-1">
                <Form.Check type="checkbox" id="edit-is-gas"     label="Gas"     checked={form.is_gas}     onChange={toggle("is_gas")} />
                <Form.Check type="checkbox" id="edit-is-propane" label="Propane" checked={form.is_propane} onChange={toggle("is_propane")} />
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Save Changes"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal show={modal === "delete"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Location</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {actionError && <Alert variant="danger" className="py-2">{actionError}</Alert>}
          <p>
            Delete <strong>{target?.name}</strong>?
            {target?.is_gas && target?.is_propane
              ? " This will remove it from both the Gas and Propane lists."
              : target?.is_gas
              ? " This will remove it from the Gas list."
              : " This will remove it from the Propane list."}
          </p>
          <p className="text-danger mb-0">This cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? <Spinner animation="border" size="sm" /> : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
